import React, { FC, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { StepTemplate } from '../../components/styled/stepTemplate/StepTemplate'
import { createValuationPdf } from '../../store/valuationPdf/thunks'
import { Status } from '../../@types/status'
import { isOwnerOrPartialOwner } from '../../utils/ownership'

import './PdfLoadingStep.scss'
import { useReportViewEvent } from '../../hooks'
import { StatusMessage } from '../../components/styled/StatusMessage/StatusMessage'
import { Loader } from '../../components/styled/Loader/StyledLoader'
import { IntegrationType } from '../../@types/config'
import { ReduxState } from '../../store/reducers'
import { getIsPdfWordingEnabled } from '../../utils/pdf'
import { enrichLead } from '../../store/lead/thunks'

interface PdfLoadingStepProps {
  goToNextStep: () => void
  goToErrorStep: () => void
}

const statusMessageHeadline = <div className='padding-bottom-s'>{`Download in "Meine Immobilien"`}</div>
const statusMessageContent = (
  <div className='status-message-content'>
    {`Du wirst in Kürze zu "Meine Immobilien" weitergeleitet. Dort steht das PDF zum Download bereit.`}
  </div>
)

export const PdfLoadingStep: FC<PdfLoadingStepProps> = (props) => {
  useReportViewEvent({ pageTitle: 'pdfloading' })
  const {
    config: { theme, integrationType, isIframe },
    lead: { id: leadId, realEstateType, ownership },
    flow: { wizardFlowType },
    valuationPdf: { data, status },
  } = useSelector((state: ReduxState) => state)
  const pdfId = data?.pdfId
  const isPdfWordingChangeEnabled = getIsPdfWordingEnabled(wizardFlowType, realEstateType)

  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(createValuationPdf())
  }, [])

  useEffect(() => {
    if (leadId) {
      dispatch(
        enrichLead({
          pageTitle: 'pdfLoading',
          request: {
            id: leadId,
            flowComplete: true,
          },
        })
      )
    }
  }, [leadId])

  useEffect(() => {
    if (status === Status.ERROR || (status === Status.SUCCESS && !pdfId)) {
      props.goToErrorStep()
    } else if (status === Status.SUCCESS && pdfId) {
      props.goToNextStep()
    }
  }, [status, pdfId])

  const showStatusMessage = () =>
    isOwnerOrPartialOwner(ownership) &&
    ((integrationType === IntegrationType.IMMOBILIENSCOUT24 && isIframe) || !isIframe) &&
    integrationType !== IntegrationType.IMMOVERKAUF24

  return (
    <StepTemplate
      headline={isPdfWordingChangeEnabled ? 'Deine Bewertung wird erstellt' : 'Das Bewertungs-PDF wird erstellt'}
      icon={<div />}
      displayNext={false}
      backgroundColor={theme.background}
      textColor={theme.text}
      secondaryLight={theme.secondaryLight}
    >
      <div data-testing='pdf-loading-container' data-testid='pdf-loading-container' className='pdf-loading-container'>
        <div className='grid-flex grid-justify-center padding-bottom-xl'>
          <div className='grid-item'>
            <div className='loader-container'>
              <Loader color={theme.secondary} />
            </div>
          </div>
        </div>

        {showStatusMessage() && (
          <div className='grid-flex grid-justify-center padding-bottom-xl'>
            <div className='grid-item desk-one-half lap-one-full palm-one-full'>
              <div data-testing='status-message-container' data-testid='status-message-container'>
                <StatusMessage
                  headline={statusMessageHeadline}
                  statusType='info'
                  statusStyle='toast'
                  message={statusMessageContent}
                  textColor={theme.text}
                  borderColor={theme.primary}
                  backgroundColor={theme.background}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </StepTemplate>
  )
}

export default PdfLoadingStep
